// Completion data for MethodScript language
// This file provides completion and hover data for the Monaco editor

// Raw completion data (plain objects, no Monaco dependencies)
export const rawData = {
    keywords: [
        { label: 'var', doc: 'Declare a variable. Usage: var <name>', snippet: 'var ${1:name}' },
        { label: 'array', doc: 'Declare an array. Usage: array <name> <size>', snippet: 'array ${1:name} ${2:size}' },
        { label: 'if', doc: 'Conditional statement. Usage: if <condition>', snippet: 'if ${1:condition}\n\t$0\nendif' },
        {
            label: 'elseif',
            doc: 'Alternative conditional. Usage: elseif <condition>',
            snippet: 'elseif ${1:condition}',
        },
        { label: 'else', doc: 'Default conditional branch. Usage: else', snippet: 'else' },
        { label: 'endif', doc: 'End of conditional block', snippet: 'endif' },
        {
            label: 'loop',
            doc: 'Start a loop. Usage: loop <iterations>',
            snippet: 'loop ${1:iterations}\n\t$0\nendloop',
        },
        { label: 'endloop', doc: 'End of loop block', snippet: 'endloop' },
        { label: 'breakloop', doc: 'Exit the current loop', snippet: 'breakloop' },
        { label: 'on_finished:', doc: 'Label for code to execute when measurement finishes', snippet: 'on_finished:' },
    ],

    measurementCommands: [
        {
            label: 'meas_loop_ca',
            doc: 'Chronoamperometry measurement loop. Applies a constant potential and measures current over time.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- dc_potential (var/literal, float): DC potential to apply\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n\n\nMust be terminated with endloop.',
            snippet: 'meas_loop_ca ${1:set_potential} ${2:measured_current} ${3:dc_potential} ${4:interval_time} ${5:run_time}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_ca_alt_mux',
            doc: 'Chronoamperometry with multiplexer. Same as meas_loop_ca but with multiplexer support.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- dc_potential (var/literal, float): DC potential to apply\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n\n\nMust be terminated with endloop.',
            snippet: 'meas_loop_ca_alt_mux ${1:set_potential} ${2:measured_current} ${3:dc_potential} ${4:interval_time} ${5:run_time}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_cv',
            doc: 'Cyclic voltammetry measurement loop. Sweeps potential between vertices and measures current.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- begin_potential (var/literal, float): Starting potential\n- vertex1_potential (var/literal, float): First vertex potential\n- vertex2_potential (var/literal, float): Second vertex potential\n- step_potential (var/literal, float): Potential step size\n- scan_rate (var/literal, float): Scan rate\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_cv ${1:set_potential} ${2:measured_current} ${3:begin_potential} ${4:vertex1_potential} ${5:vertex2_potential} ${6:step_potential} ${7:scan_rate}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_lsv',
            doc: 'Linear Sweep Voltammetry measurement loop. Sweeps potential linearly and measures current.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- begin_potential (var/literal, float): Starting potential\n- end_potential (var/literal, float): Ending potential\n- step_potential (var/literal, float): Potential step size\n- scan_rate (var/literal, float): Scan rate\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_lsv ${1:set_potential} ${2:measured_current} ${3:begin_potential} ${4:end_potential} ${5:step_potential} ${6:scan_rate}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_dpv',
            doc: 'Differential Pulse Voltammetry measurement loop.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- begin_potential (var/literal, float): Starting potential\n- end_potential (var/literal, float): Ending potential\n- step_potential (var/literal, float): Potential step size\n- pulse_potential (var/literal, float): Pulse amplitude\n- pulse_time (var/literal, float): Pulse duration\n- scan_rate (var/literal, float): Scan rate\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_dpv ${1:set_potential} ${2:measured_current} ${3:begin_potential} ${4:end_potential} ${5:step_potential} ${6:pulse_potential} ${7:pulse_time} ${8:scan_rate}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_swv',
            doc: 'Square Wave Voltammetry measurement loop.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- forward_current (var): Variable to store forward current\n- reverse_current (var): Variable to store reverse current\n- begin_potential (var/literal, float): Starting potential\n- end_potential (var/literal, float): Ending potential\n- step_potential (var/literal, float): Potential step size\n- amplitude (var/literal, float): Pulse amplitude\n- frequency (var/literal, float): Pulse frequency\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_swv ${1:set_potential} ${2:measured_current} ${3:forward_current} ${4:reverse_current} ${5:begin_potential} ${6:end_potential} ${7:step_potential} ${8:amplitude} ${9:frequency}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_npv',
            doc: 'Normal Pulse Voltammetry measurement loop.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- begin_potential (var/literal, float): Starting potential\n- end_potential (var/literal, float): Ending potential\n- step_potential (var/literal, float): Potential step size\n- pulse_time (var/literal, float): Pulse duration\n- scan_rate (var/literal, float): Scan rate\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_npv ${1:set_potential} ${2:measured_current} ${3:begin_potential} ${4:end_potential} ${5:step_potential} ${6:pulse_time} ${7:scan_rate}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_pad',
            doc: 'Pulsed Amperometric Detection measurement loop.\n\n\nParameters:\n- set_potential (var): Variable to store applied potential\n- measured_current (var): Variable to store measured current\n- dc_potential (var/literal, float): DC potential\n- pulse_potential (var/literal, float): Pulse potential\n- pulse_time (var/literal, float): Pulse duration\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n- mode (var/literal, int): Mode (1=DC, 2=Pulse, 3=Differential)\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_pad ${1:set_potential} ${2:measured_current} ${3:dc_potential} ${4:pulse_potential} ${5:pulse_time} ${6:interval_time} ${7:run_time} ${8:mode}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_ocp',
            doc: 'Open Circuit Potentiometry measurement loop. Measures potential without applying current.\n\n\nParameters:\n- measured_potential (var): Variable to store measured potential\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n\n\nMust be terminated with endloop.',
            snippet: 'meas_loop_ocp ${1:measured_potential} ${2:interval_time} ${3:run_time}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_ocp_alt_mux',
            doc: 'Open Circuit Potentiometry with multiplexer. Same as meas_loop_ocp but with multiplexer support.\n\n\nParameters:\n- measured_potential (var): Variable to store measured potential\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n\n\nMust be terminated with endloop.',
            snippet: 'meas_loop_ocp_alt_mux ${1:measured_potential} ${2:interval_time} ${3:run_time}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_cp',
            doc: 'Chronopotentiometry measurement loop. Applies constant current and measures potential over time.\n\n\nParameters:\n- measured_potential (var): Variable to store measured potential\n- set_current (var): Variable to store applied current\n- dc_current (var/literal, float): DC current to apply\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n\n\nMust be terminated with endloop.',
            snippet: 'meas_loop_cp ${1:measured_potential} ${2:set_current} ${3:dc_current} ${4:interval_time} ${5:run_time}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_cp_alt_mux',
            doc: 'Chronopotentiometry with multiplexer. Same as meas_loop_cp but with multiplexer support.\n\n\nParameters:\n- measured_potential (var): Variable to store measured potential\n- set_current (var): Variable to store applied current\n- dc_current (var/literal, float): DC current to apply\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration\n\n\nMust be terminated with endloop.',
            snippet: 'meas_loop_cp_alt_mux ${1:measured_potential} ${2:set_current} ${3:dc_current} ${4:interval_time} ${5:run_time}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_lsp',
            doc: 'Linear Sweep Potentiometry measurement loop. Sweeps current linearly and measures potential.\n\n\nParameters:\n- measured_potential (var): Variable to store measured potential\n- set_current (var): Variable to store applied current\n- begin_current (var/literal, float): Starting current\n- end_current (var/literal, float): Ending current\n- step_current (var/literal, float): Current step size\n- scan_rate (var/literal, float): Scan rate\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_lsp ${1:measured_potential} ${2:set_current} ${3:begin_current} ${4:end_current} ${5:step_current} ${6:scan_rate}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_acv',
            doc: 'AC Voltammetry measurement loop.\n\n\nParameters:\n- dc_potential (var): Variable to store DC potential\n- dc_current (var): Variable to store DC current\n- ac_potential (var): Variable to store AC potential\n- ac_current (var): Variable to store AC current\n- z_real (var): Variable to store real impedance\n- z_imag (var): Variable to store imaginary impedance\n- begin_potential (var/literal, float): Starting potential\n- end_potential (var/literal, float): Ending potential\n- step_potential (var/literal, float): Potential step size\n- scan_rate (var/literal, float): Scan rate\n- amplitude (var/literal, float): AC amplitude\n- frequency (var/literal, float): AC frequency\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_acv ${1:dc_potential} ${2:dc_current} ${3:ac_potential} ${4:ac_current} ${5:z_real} ${6:z_imag} ${7:begin_potential} ${8:end_potential} ${9:step_potential} ${10:scan_rate} ${11:amplitude} ${12:frequency}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_eis',
            doc: 'Electrochemical Impedance Spectroscopy measurement loop.\n\n\nParameters:\n- frequency (var): Variable to store applied frequency\n- z_real (var): Variable to store real impedance component\n- z_imag (var): Variable to store imaginary impedance component\n- amplitude (var/literal, float): AC amplitude\n- start_frequency (var/literal, float): Starting frequency\n- end_frequency (var/literal, float): Ending frequency\n- num_points (var/literal, int): Number of measurement points\n- dc_potential (var/literal, float): DC bias potential\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_eis ${1:frequency} ${2:z_real} ${3:z_imag} ${4:amplitude} ${5:start_frequency} ${6:end_frequency} ${7:num_points} ${8:dc_potential}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_eis_dual',
            doc: 'Dual-channel Electrochemical Impedance Spectroscopy measurement loop.\n\n\nParameters:\n- mode (var/literal, int): Measurement mode\n- frequency (var): Variable to store applied frequency\n- z_real (var): Variable to store real impedance component\n- z_imag (var): Variable to store imaginary impedance component\n- z_real_2 (var): Variable to store second channel real impedance\n- z_imag_2 (var): Variable to store second channel imaginary impedance\n- amplitude (var/literal, float): AC amplitude\n- start_frequency (var/literal, float): Starting frequency\n- end_frequency (var/literal, float): Ending frequency\n- num_points (var/literal, int): Number of measurement points\n- dc_potential (var/literal, float): DC bias potential\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_eis_dual ${1:mode} ${2:frequency} ${3:z_real} ${4:z_imag} ${5:z_real_2} ${6:z_imag_2} ${7:amplitude} ${8:start_frequency} ${9:end_frequency} ${10:num_points} ${11:dc_potential}\n\t$0\nendloop',
        },
        {
            label: 'meas_loop_geis',
            doc: 'Galvanostatic Electrochemical Impedance Spectroscopy measurement loop.\n\n\nParameters:\n- frequency (var): Variable to store applied frequency\n- z_real (var): Variable to store real impedance component\n- z_imag (var): Variable to store imaginary impedance component\n- amplitude (var/literal, float): AC amplitude\n- start_frequency (var/literal, float): Starting frequency\n- end_frequency (var/literal, float): Ending frequency\n- num_points (var/literal, int): Number of measurement points\n- dc_current (var/literal, float): DC bias current\n\n\nMust be terminated with endloop.',
            snippet:
                'meas_loop_geis ${1:frequency} ${2:z_real} ${3:z_imag} ${4:amplitude} ${5:start_frequency} ${6:end_frequency} ${7:num_points} ${8:dc_current}\n\t$0\nendloop',
        },
    ],

    standardCommands: [
        { label: 'abort', doc: 'Abort the current script and jump to on_finished: block' },
        {
            label: 'add_var',
            doc: 'Add values to a variable.\n\n\nParameters:\n- arg1 (var): Variable to modify in place\n- arg2 (var/literal): Value to add to arg1',
            snippet: 'add_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'alter_vartype',
            doc: 'Change variable type.\n\n\nParameters:\n- variable (var): Variable to modify\n- vartype (VarType): New variable type',
            snippet: 'alter_vartype ${1:variable} ${2:vartype}',
        },
        {
            label: 'array_get',
            doc: 'Get value from array.\n\n\nParameters:\n- array (array): Source array\n- index (var/literal, int): Array index\n- result (var): Variable to store the value',
            snippet: 'array_get ${1:array} ${2:index} ${3:result}',
        },
        {
            label: 'array_set',
            doc: 'Set value in array.\n\n\nParameters:\n- array (array): Target array\n- index (var/literal, int): Array index\n- value (var/literal): Value to store',
            snippet: 'array_set ${1:array} ${2:index} ${3:value}',
        },
        { label: 'await_int', doc: 'Pause until the next configured interval' },
        {
            label: 'beep',
            doc: 'Produce a beep sound.\n\n\nParameters:\n- tone (uint8): Tone frequency\n- volume (uint8): Volume level\n- duration (float): Duration in seconds',
            snippet: 'beep ${1:tone} ${2:volume} ${3:duration}',
        },
        {
            label: 'bit_and_var',
            doc: 'Bitwise AND operation.\n\n\nParameters:\n- arg1 (var, int): Variable to modify in place\n- arg2 (var/literal, int): Value to AND with arg1',
            snippet: 'bit_and_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'bit_inv_var',
            doc: 'Bitwise inversion (NOT).\n\n\nParameters:\n- variable (var, int): Variable to invert in place',
            snippet: 'bit_inv_var ${1:variable}',
        },
        {
            label: 'bit_lsl_var',
            doc: 'Bitwise left shift.\n\n\nParameters:\n- arg1 (var, int): Variable to shift in place\n- arg2 (var/literal, int): Number of positions to shift',
            snippet: 'bit_lsl_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'bit_lsr_var',
            doc: 'Bitwise right shift.\n\n\nParameters:\n- arg1 (var, int): Variable to shift in place\n- arg2 (var/literal, int): Number of positions to shift',
            snippet: 'bit_lsr_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'bit_or_var',
            doc: 'Bitwise OR operation.\n\n\nParameters:\n- arg1 (var, int): Variable to modify in place\n- arg2 (var/literal, int): Value to OR with arg1',
            snippet: 'bit_or_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'bit_xor_var',
            doc: 'Bitwise XOR operation.\n\n\nParameters:\n- arg1 (var, int): Variable to modify in place\n- arg2 (var/literal, int): Value to XOR with arg1',
            snippet: 'bit_xor_var ${1:arg1} ${2:arg2}',
        },
        { label: 'cell_off', doc: 'Turn off the electrochemical cell (disable cell)' },
        { label: 'cell_on', doc: 'Turn on the electrochemical cell (enable cell and start regulation)' },
        {
            label: 'copy_var',
            doc: 'Copy a variable (value and metadata).\n\n\nParameters:\n- source (var): Source variable\n- dest (var): Destination variable',
            snippet: 'copy_var ${1:source} ${2:dest}',
        },
        {
            label: 'div_var',
            doc: 'Divide values.\n\n\nParameters:\n- arg1 (var): Variable to modify in place\n- arg2 (var/literal): Value to divide arg1 by',
            snippet: 'div_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'file_close',
            doc: 'Close a file.\n\n\nParameters:\n- handle (var, int): File handle to close',
            snippet: 'file_close ${1:handle}',
        },
        {
            label: 'file_open',
            doc: 'Open a file.\n\n\nParameters:\n- filename (string): Name of file to open\n- mode (uint8): Open mode (0=create new, 1=append, 2=create new error if exists)',
            snippet: 'file_open "${1:filename}" ${2:mode}',
        },
        {
            label: 'float_to_int',
            doc: 'Convert float to integer (truncates).\n\n\nParameters:\n- variable (var): Variable to convert in place',
            snippet: 'float_to_int ${1:variable}',
        },
        {
            label: 'get_gpio',
            doc: 'Read GPIO pin state.\n\n\nParameters:\n- pin_mask (var): Variable to store pin states',
            snippet: 'get_gpio ${1:pin_mask}',
        },
        {
            label: 'get_gpio_msk',
            doc: 'Read GPIO pins with mask.\n\n\nParameters:\n- mask (var/literal, int): Pin mask\n- values (var): Variable to store masked values',
            snippet: 'get_gpio_msk ${1:mask} ${2:values}',
        },
        {
            label: 'get_time',
            doc: 'Get time since device startup.\n\n\nParameters:\n- result (var, float): Variable to store time in seconds',
            snippet: 'get_time ${1:result}',
        },
        {
            label: 'hibernate',
            doc: 'Enter low-power hibernation mode.\n\n\nParameters:\n- wake_mask (uint8): Wake-up source mask\n- wake_time (var/literal, float): Wake-up time in seconds',
            snippet: 'hibernate ${1:wake_mask} ${2:wake_time}',
        },
        {
            label: 'i2c_config',
            doc: 'Configure I2C interface.\n\n\nParameters:\n- clock_speed (var/literal): Clock speed\n- address_mode (literal): Address mode',
            snippet: 'i2c_config ${1:clock_speed} ${2:address_mode}',
        },
        {
            label: 'i2c_read',
            doc: 'Read block from I2C device.\n\n\nParameters:\n- device_addr (var/literal, int): I2C device address\n- rx_data (array): Array to store received data\n- rx_count (var/literal, int): Number of bytes to read\n- ack_status (var): ACK status variable',
            snippet: 'i2c_read ${1:device_addr} ${2:rx_data} ${3:rx_count} ${4:ack_status}',
        },
        {
            label: 'i2c_read_byte',
            doc: 'Read single byte from I2C.\n\n\nParameters:\n- device_addr (var/literal, int): I2C device address\n- rx_data (var, int): Variable to store received byte\n- ack_status (var, int): ACK status variable',
            snippet: 'i2c_read_byte ${1:device_addr} ${2:rx_data} ${3:ack_status}',
        },
        {
            label: 'i2c_write',
            doc: 'Write block to I2C device.\n\n\nParameters:\n- device_addr (var/literal, int): I2C device address\n- tx_data (array, int): Array with data to transmit\n- tx_count (var/literal, int): Number of bytes to write\n- ack_status (var): ACK status variable',
            snippet: 'i2c_write ${1:device_addr} ${2:tx_data} ${3:tx_count} ${4:ack_status}',
        },
        {
            label: 'i2c_write_byte',
            doc: 'Write single byte to I2C.\n\n\nParameters:\n- device_addr (var/literal, int): I2C device address\n- tx_data (var/literal, int): Byte to transmit\n- ack_status (var, int): ACK status variable',
            snippet: 'i2c_write_byte ${1:device_addr} ${2:tx_data} ${3:ack_status}',
        },
        {
            label: 'i2c_write_read',
            doc: 'Write then read from I2C.\n\n\nParameters:\n- device_addr: I2C device address\n- tx_data (array, int): Array with data to transmit\n- tx_count: Number of bytes to write\n- rx_data (array): Array for received data\n- rx_count: Number of bytes to read\n- ack_status (var): ACK status variable',
            snippet: 'i2c_write_read ${1:device_addr} ${2:tx_data} ${3:tx_count} ${4:rx_data} ${5:rx_count} ${6:ack_status}',
        },
        {
            label: 'int_to_float',
            doc: 'Convert integer to float.\n\n\nParameters:\n- variable (var): Variable to convert in place',
            snippet: 'int_to_float ${1:variable}',
        },
        {
            label: 'meas',
            doc: 'Measure a data point of the specified type and store the result as a variable. The data point will be averaged for the specified amount of time at the maximum available sampling rate.\n\n\nArguments:\n- Duration (float): The amount of time to spend averaging measured data\n- Destination (var): Variable to store the measured data in\n- VarType: The type of variable to measure (e.g., ba for VT_CURRENT)\n\n\nExample: meas 100m c ba',
            snippet: 'meas ${1:duration} ${2:variable} ${3:vartype}',
        },
        {
            label: 'meas_fast_ca',
            doc: 'Fast chronoamperometry measurement. Stores all results in arrays for high-speed data acquisition.\n\n\nParameters:\n- time_array (var): Array to store time values\n- potential_array (var): Array to store potential values\n- current_array (var): Array to store current values\n- dc_potential (var/literal, float): DC potential to apply\n- interval_time (var/literal, float): Time between measurements\n- run_time (var/literal, float): Total measurement duration',
            snippet: 'meas_fast_ca ${1:time_array} ${2:potential_array} ${3:current_array} ${4:dc_potential} ${5:interval_time} ${6:run_time}',
        },
        {
            label: 'meas_fast_cv',
            doc: 'Fast cyclic voltammetry measurement. Stores all results in arrays for high-speed data acquisition.\n\n\nParameters:\n- time_array (var): Array to store time values\n- potential_array (var): Array to store potential values\n- current_array (var): Array to store current values\n- begin_potential (var/literal, float): Starting potential\n- vertex1_potential (var/literal, float): First vertex potential\n- vertex2_potential (var/literal, float): Second vertex potential\n- step_potential (var/literal, float): Potential step size\n- scan_rate (var/literal, float): Scan rate',
            snippet:
                'meas_fast_cv ${1:time_array} ${2:potential_array} ${3:current_array} ${4:begin_potential} ${5:vertex1_potential} ${6:vertex2_potential} ${7:step_potential} ${8:scan_rate}',
        },
        {
            label: 'meas_ms_eis',
            doc: 'Multi-sine Electrochemical Impedance Spectroscopy measurement. Measures impedance at multiple frequencies simultaneously.\n\n\nParameters:\n- frequency_array (var): Array to store frequency values\n- z_real_array (var): Array to store real impedance components\n- z_imag_array (var): Array to store imaginary impedance components\n- amplitude (var/literal, float): AC amplitude\n- start_frequency (var/literal, float): Starting frequency\n- end_frequency (var/literal, float): Ending frequency\n- num_points (var/literal, int): Number of measurement points\n- dc_potential (var/literal, float): DC bias potential',
            snippet:
                'meas_ms_eis ${1:frequency_array} ${2:z_real_array} ${3:z_imag_array} ${4:amplitude} ${5:start_frequency} ${6:end_frequency} ${7:num_points} ${8:dc_potential}',
        },
        {
            label: 'mod_var',
            doc: 'Modulo operation.\n\n\nParameters:\n- arg1 (var, int): Variable to modify in place\n- arg2 (var/literal, int): Modulo value',
            snippet: 'mod_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'mul_var',
            doc: 'Multiply values.\n\n\nParameters:\n- arg1 (var): Variable to modify in place\n- arg2 (var/literal): Value to multiply with arg1',
            snippet: 'mul_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'mux_config',
            doc: 'Configure multiplexer.\n\n\nParameters:\n- mux_type (var/literal, int): Multiplexer type\n- config (literal, int): Configuration bitmask',
            snippet: 'mux_config ${1:mux_type} ${2:config}',
        },
        {
            label: 'mux_get_channel_count',
            doc: 'Get multiplexer channel count.\n\n\nParameters:\n- result (var, int): Variable to store channel count',
            snippet: 'mux_get_channel_count ${1:result}',
        },
        {
            label: 'mux_set_channel',
            doc: 'Set active multiplexer channel.\n\n\nParameters:\n- channel (var/literal, int): Channel number to select',
            snippet: 'mux_set_channel ${1:channel}',
        },
        {
            label: 'notify_led',
            doc: 'Control notification LED.\n\n\nParameters:\n- mode (uint16): Notification mode (0=clear, 1=idle, 2=busy, 3=attention, etc.)',
            snippet: 'notify_led ${1:mode}',
        },
        {
            label: 'pck_add',
            doc: 'Add variable or literal to current data packet.\n\n\nParameters:\n- data (var/literal): Data to add to packet',
            snippet: 'pck_add ${1:data}',
        },
        { label: 'pck_end', doc: 'Transmit the completed data packet' },
        {
            label: 'pck_start',
            doc: 'Start a new measurement data packet. Must be closed with pck_end',
            snippet: 'pck_start\n\t$0\npck_end',
        },
        {
            label: 'peak_detect',
            doc: 'Detect peaks in data array.\n\n\nParameters:\n- input_array (array): Input data\n- output_indices (array, int): Array to store peak indices\n- output_heights (array, float): Array to store peak heights\n- direction (var/literal, int): Peak direction\n- threshold (var/literal, float): Detection threshold',
            snippet: 'peak_detect ${1:input_array} ${2:output_indices} ${3:output_heights} ${4:direction} ${5:threshold}',
        },
        {
            label: 'rtc_get',
            doc: 'Get real-time clock value.\n\n\nParameters:\n- year (var, int): Year\n- month (var, int): Month\n- day (var, int): Day\n- hour (var, int): Hour\n- minute (var, int): Minute\n- second (var, int): Second',
            snippet: 'rtc_get ${1:year} ${2:month} ${3:day} ${4:hour} ${5:minute} ${6:second}',
        },
        {
            label: 'send_string',
            doc: 'Send arbitrary string to output.\n\n\nParameters:\n- text (string): String to send (supports f-string interpolation)',
            snippet: 'send_string "${1:text}"',
        },
        {
            label: 'set_acquisition_frac',
            doc: 'Set acquisition fraction.\n\n\nParameters:\n- fraction (var/literal, float): Acquisition fraction',
            snippet: 'set_acquisition_frac ${1:fraction}',
        },
        { label: 'set_acquisition_frac_autoadjust', doc: 'Enable auto-adjustment of acquisition fraction' },
        {
            label: 'set_autoranging',
            doc: 'Configure autoranging for measurement loops.\n\n\nParameters:\n- vartype (VarType): Variable type\n- min (var/literal, float): Minimum value\n- max (var/literal, float): Maximum value',
            snippet: 'set_autoranging ${1:vartype} ${2:min} ${3:max}',
        },
        {
            label: 'set_bipot_mode',
            doc: 'Set bipotentiostat mode.\n\n\nParameters:\n- mode (uint8): BiPot mode',
            snippet: 'set_bipot_mode ${1:mode}',
        },
        {
            label: 'set_bipot_potential',
            doc: 'Set potential or offset for second WE.\n\n\nParameters:\n- potential (var/literal, float): Potential value',
            snippet: 'set_bipot_potential ${1:potential}',
        },
        {
            label: 'set_channel_sync',
            doc: 'Enable/disable measurement synchronization between channels.\n\n\nParameters:\n- sync_enable (uint8): Sync enable flag',
            snippet: 'set_channel_sync ${1:sync_enable}',
        },
        {
            label: 'set_e',
            doc: 'Set electrode potential (apply DC potential to working electrode).\n\n\nParameters:\n- potential (var/literal, float): Voltage to apply (e.g., 500m for 0.5V)',
            snippet: 'set_e ${1:potential}',
        },
        {
            label: 'set_e_aux',
            doc: 'Set voltage on auxiliary DAC.\n\n\nParameters:\n- voltage (var/literal, float): Voltage to set',
            snippet: 'set_e_aux ${1:voltage}',
        },
        {
            label: 'set_gpio',
            doc: 'Set output values for all GPIO pins.\n\n\nParameters:\n- values (var/literal, int): Output values for all pins',
            snippet: 'set_gpio ${1:values}',
        },
        {
            label: 'set_gpio_cfg',
            doc: 'Configure GPIO pins.\n\n\nParameters:\n- pin_mask (uint32): Pin mask\n- mode (uint8): Mode (input/output/peripheral)',
            snippet: 'set_gpio_cfg ${1:pin_mask} ${2:mode}',
        },
        {
            label: 'set_gpio_msk',
            doc: 'Write to masked subset of GPIO pins.\n\n\nParameters:\n- mask (var/literal, int): Pin mask\n- values (var/literal, int): Values to set',
            snippet: 'set_gpio_msk ${1:mask} ${2:values}',
        },
        {
            label: 'set_gpio_pullup',
            doc: 'Enable/disable pull-up resistors on GPIO pins.\n\n\nParameters:\n- pin_mask (uint32): Pin mask\n- pullup (uint8): Pull-up enable flag',
            snippet: 'set_gpio_pullup ${1:pin_mask} ${2:pullup}',
        },
        {
            label: 'set_i',
            doc: 'Set current (apply DC current in galvanostatic mode).\n\n\nParameters:\n- current (var/literal, float): Current to apply in amperes',
            snippet: 'set_i ${1:current}',
        },
        {
            label: 'set_int',
            doc: 'Configure interval for await_int.\n\n\nParameters:\n- interval (var/literal, float): Interval in seconds',
            snippet: 'set_int ${1:interval}',
        },
        {
            label: 'set_ir_comp',
            doc: 'Set resistance for iR compensation.\n\n\nParameters:\n- resistance (var/literal, float): Resistance value',
            snippet: 'set_ir_comp ${1:resistance}',
        },
        {
            label: 'set_max_bandwidth',
            doc: 'Set maximum bandwidth of measurement signal.\n\n\nParameters:\n- bandwidth (var/literal, float): Maximum bandwidth',
            snippet: 'set_max_bandwidth ${1:bandwidth}',
        },
        {
            label: 'set_pgstat_chan',
            doc: 'Select active PGStat channel.\n\n\nParameters:\n- channel (uint8): Channel index',
            snippet: 'set_pgstat_chan ${1:channel}',
        },
        {
            label: 'set_pgstat_mode',
            doc: 'Set hardware configuration mode.\n\n\nParameters:\n- mode (uint8): PGStat mode (0=off, 2=low speed, 3=high speed, etc.)',
            snippet: 'set_pgstat_mode ${1:mode}',
        },
        {
            label: 'set_poly_we_mode',
            doc: 'Select mode of additional WE (legacy).\n\n\nParameters:\n- mode (uint8): Poly WE mode',
            snippet: 'set_poly_we_mode ${1:mode}',
        },
        {
            label: 'set_range',
            doc: 'Set expected measurement range.\n\n\nParameters:\n- vartype (VarType): Variable type\n- max_value (var/literal, float): Maximum expected value',
            snippet: 'set_range ${1:vartype} ${2:max_value}',
        },
        {
            label: 'set_range_minmax',
            doc: 'Set expected min/max measurement range.\n\n\nParameters:\n- vartype (VarType): Variable type\n- min_value (var/literal, float): Minimum expected value\n- max_value (var/literal, float): Maximum expected value',
            snippet: 'set_range_minmax ${1:vartype} ${2:min_value} ${3:max_value}',
        },
        {
            label: 'set_scan_dir',
            doc: 'Set scan direction.\n\n\nParameters:\n- direction (var/literal, int): Scan direction',
            snippet: 'set_scan_dir ${1:direction}',
        },
        {
            label: 'set_script_output',
            doc: 'Configure script output destination.\n\n\nParameters:\n- mode (uint8): Output mode (0=disable, 1=normal channel, 2=file, 3=both)',
            snippet: 'set_script_output ${1:mode}',
        },
        {
            label: 'smooth',
            doc: 'Apply Savitzky-Golay smoothing to data.\n\n\nParameters:\n- input_array (array): Input data\n- output_array (array): Output smoothed data\n- smoothness (var/literal, int): Smoothness parameter',
            snippet: 'smooth ${1:input_array} ${2:output_array} ${3:smoothness}',
        },
        {
            label: 'store_var',
            doc: 'Store literal value and type in variable.\n\n\nParameters:\n- variable (var): Variable to store to\n- value (literal): Value to store\n- vartype (VarType): Variable type',
            snippet: 'store_var ${1:variable} ${2:value} ${3:vartype}',
        },
        {
            label: 'sub_var',
            doc: 'Subtract values.\n\n\nParameters:\n- arg1 (var): Variable to modify in place\n- arg2 (var/literal): Value to subtract from arg1',
            snippet: 'sub_var ${1:arg1} ${2:arg2}',
        },
        {
            label: 'timer_get',
            doc: 'Get elapsed time since last timer_start.\n\n\nParameters:\n- result (var, float): Variable to store elapsed time',
            snippet: 'timer_get ${1:result}',
        },
        { label: 'timer_start', doc: 'Start or reset high-resolution timer' },
        {
            label: 'wait',
            doc: 'Pause script execution.\n\n\nParameters:\n- time (var/literal, float): Duration to wait in seconds (e.g., 100m for 0.1 seconds)',
            snippet: 'wait ${1:time}',
        },
    ],
};

// Get hover documentation for a word
export function getHoverDocumentation(word: string): { contents: Array<{ value: string }> } | null {
    // Search in all categories
    for (const category of [rawData.keywords, rawData.measurementCommands, rawData.standardCommands]) {
        const item = category.find((i) => i.label === word);
        if (item) {
            return {
                contents: [{ value: `**${item.label}**` }, { value: item.doc }],
            };
        }
    }

    return null;
}
